import React, { useMemo } from 'react';
import {
  IconFilter,
  IconLocation,
  IconStar,
  IconTime,
} from '@americanexpress/dls-icons';
import { Tag, Link } from '@americanexpress/dls-react';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { formatText } from '@/helpers/utils';
import PropTypes from 'prop-types';
import { amenityIdToIconMap } from '@/constants/amenityIconMapping';
import {
  airportFilterCategories,
  filterConstantValues,
} from '@/constants/airportFilterConstants';
import { hasFilters, filterLounges } from '@/helpers/airportFiltersHelper';
import {
  FilterMenu,
  MenuGroup,
  MenuItem,
  MenuTypes,
} from '../common/FilterMenu';
import { IconLounge } from '../icons';

const AirportFilters = ({
  lounges,
  localFilters,
  appliedFilters,
  setLocalFilters,
  applyFilters,
}) => {
  const { customTrack } = useAnalyticsContext();
  const {
    loungeFilters,
    lounges: { open },
  } = useCommonContent();

  const airportCode = lounges?.[0]?.airport?.iata;
  const { terminalList, amenitiesList } = useMemo(() => {
    const menuOptions = {
      terminal: new Set(),
      amenities: new Map(),
    };

    lounges.forEach((lounge) => {
      menuOptions.terminal.add(lounge.location.terminal);

      lounge?.amenities?.forEach((amenityObj) => {
        const nonFreeAmenityId = amenityObj.id.split('_FREE')[0]; // Removed '_FREE' suffix from ID so that both free and paid amenities are grouped together
        if (!menuOptions.amenities.has(nonFreeAmenityId)) {
          menuOptions.amenities.set(nonFreeAmenityId, {
            ...amenityObj,
            id: nonFreeAmenityId,
          });
        }
      });
    });

    const sortedAmenities = [...menuOptions.amenities.values()].sort(
      (a, b) => a.rank - b.rank
    );

    return {
      terminalList: [...menuOptions.terminal],
      amenitiesList: sortedAmenities,
    };
  }, [lounges]);

  const modifyFilterData = (filterData, category, option) => {
    const setForCategory = new Set(filterData[category]);
    if (setForCategory.has(option)) setForCategory.delete(option);
    else setForCategory.add(option);
    return { ...filterData, [category]: [...setForCategory] };
  };

  const toggleOption = ({ event, category, option, isSelected }) => {
    if (isSelected) {
      customTrack({
        event,
        element: `remove filters>>filter modal`,
        detail: option,
      });
    } else {
      customTrack({
        event,
        element: `filter>>${category}`,
        detail: option,
      });
    }

    setLocalFilters((prev) => {
      return modifyFilterData(prev, category, option);
    });
  };

  const removeFilterTags = (e, category, option) => {
    customTrack({
      event: e,
      element: `remove filters>>airport page`,
      detail: option,
    });
    const updatedFilters = modifyFilterData(appliedFilters, category, option);
    applyFilters(updatedFilters);
  };

  const getTerminalMenuItems = (category) => {
    return terminalList.map((terminal) => {
      const isSelected = Boolean(localFilters?.[category]?.includes(terminal));

      return (
        <MenuItem
          key={terminal}
          label={terminal}
          isSelected={isSelected}
          onClick={(e) => {
            toggleOption({ event: e, category, option: terminal, isSelected });
          }}
        />
      );
    });
  };

  const getAmenitiesMenuItems = (category) => {
    return amenitiesList.map((amenity) => {
      const TheIcon = amenityIdToIconMap[amenity.id] || IconLounge;
      const isSelected = Boolean(
        localFilters?.[category]?.includes(amenity.id)
      );

      return (
        <MenuItem
          key={amenity.id}
          label={amenity.name}
          isSelected={isSelected}
          onClick={(e) => {
            toggleOption({
              event: e,
              category,
              option: amenity.id,
              isSelected,
            });
          }}
          icon={
            <TheIcon
              className="tw-mr-2"
              color="inherit"
              size="md"
              variant="outline"
            />
          }
        />
      );
    });
  };

  const isHoursSelected = (filters) => {
    return (filters?.[airportFilterCategories.HOURS] || []).includes(
      filterConstantValues.OPEN_NOW
    );
  };

  const isComplimentaryOnlySelected = (filters) => {
    return (filters?.[airportFilterCategories.AMENITIES_COST] || []).includes(
      filterConstantValues.COMPLIMENTARY_ONLY
    );
  };

  const hasAppliedFilters = hasFilters(appliedFilters);
  const filteredLounges = useMemo(
    () => filterLounges(lounges, localFilters),
    [lounges, localFilters]
  );

  const applyButtonText =
    filteredLounges?.length === 1
      ? loungeFilters?.actions?.applySingular || ''
      : formatText(loungeFilters?.actions?.applyMultiple || '', {
          length: filteredLounges?.length || 0,
        });

  // Reset position on nested spans created for screen readers (sr-only)
  // Absolute positioning was breaking flex width calculation and causing page overflow
  const tagsClassNames =
    'tw-flex-shrink-0 span-children_span-children_tw-static tw-py-1';

  const applyFiltersWithTracking = (event, trackingPayload) => {
    const noLounges = filteredLounges?.length === 0;
    if (noLounges) {
      customTrack({
        event,
        element: airportCode,
        detail: 'No Filter Results',
      });
    } else {
      const {
        detail = `apply filter`,
        element = `number of lounges: ${filteredLounges?.length}`,
      } = trackingPayload;
      customTrack({
        event,
        element,
        detail,
      });
    }
    applyFilters();
  };

  const clearLocalFiltersByCategory = (categoryList) => {
    if (categoryList?.length > 0) {
      const updatedFilters = { ...localFilters };
      categoryList.forEach((category) => {
        updatedFilters[category] = [];
      });
      setLocalFilters(updatedFilters);
    } else {
      setLocalFilters({});
    }
  };

  const resetLocalFilter = () => {
    setLocalFilters(appliedFilters);
  };

  return (
    <>
      <div className="scrollbar-hide -tw-mx-4 tw-mb-4 tw-flex tw-gap-2 tw-overflow-x-auto tw-whitespace-nowrap tw-px-4 md_tw-mx-0 md_tw-overflow-visible md_tw-px-0">
        <FilterMenu
          TriggerIcon={IconFilter}
          menuTitle={loungeFilters?.filters}
          onApplyClick={applyFiltersWithTracking}
          onResetClick={(e) => {
            e.stopPropagation();
            clearLocalFiltersByCategory();
          }}
          onMenuClose={resetLocalFilter}
          isFilterApplied={hasAppliedFilters}
          applyButtonText={applyButtonText}
          resetButtonText={loungeFilters?.actions?.reset}
        >
          <MenuGroup title={loungeFilters?.terminal}>
            {getTerminalMenuItems(airportFilterCategories.TERMINAL)}
          </MenuGroup>
          <MenuGroup title={loungeFilters?.amenities}>
            {getAmenitiesMenuItems(airportFilterCategories.AMENITIES)}
            <MenuItem
              label={loungeFilters?.complimentary?.toggleText}
              type={MenuTypes.TOGGLE}
              hintText={loungeFilters?.complimentary?.toggleHint}
              isSelected={isComplimentaryOnlySelected(localFilters)}
              onClick={(e) => {
                toggleOption({
                  event: e,
                  category: airportFilterCategories.AMENITIES_COST,
                  option: filterConstantValues.COMPLIMENTARY_ONLY,
                  isSelected: isComplimentaryOnlySelected(localFilters),
                });
              }}
            />
          </MenuGroup>
          <MenuGroup title={loungeFilters?.hours}>
            <MenuItem
              label={open}
              type={MenuTypes.TOGGLE}
              isSelected={isHoursSelected(localFilters)}
              onClick={(e) => {
                toggleOption({
                  event: e,
                  category: airportFilterCategories.HOURS,
                  option: filterConstantValues.OPEN_NOW,
                  isSelected: isHoursSelected(localFilters),
                });
              }}
            />
          </MenuGroup>
        </FilterMenu>

        <FilterMenu
          TriggerIcon={IconLocation}
          triggerTitle={loungeFilters?.terminal}
          menuTitle={loungeFilters?.terminal}
          onApplyClick={applyFiltersWithTracking}
          onResetClick={() => {
            clearLocalFiltersByCategory([airportFilterCategories.TERMINAL]);
          }}
          onMenuClose={resetLocalFilter}
          isFilterApplied={!!appliedFilters?.terminal?.length}
          applyButtonText={applyButtonText}
          resetButtonText={loungeFilters?.actions?.reset}
        >
          <MenuGroup title={loungeFilters?.terminal} showTitle={false}>
            {getTerminalMenuItems(airportFilterCategories.TERMINAL)}
          </MenuGroup>
        </FilterMenu>

        <FilterMenu
          TriggerIcon={IconStar}
          triggerTitle={loungeFilters?.amenities}
          menuTitle={loungeFilters?.amenities}
          onApplyClick={applyFiltersWithTracking}
          onResetClick={() => {
            clearLocalFiltersByCategory([
              airportFilterCategories.AMENITIES,
              airportFilterCategories.AMENITIES_COST,
            ]);
          }}
          onMenuClose={resetLocalFilter}
          isFilterApplied={
            !!appliedFilters?.amenities?.length ||
            isComplimentaryOnlySelected(appliedFilters)
          }
          applyButtonText={applyButtonText}
          resetButtonText={loungeFilters?.actions?.reset}
        >
          <MenuGroup title={loungeFilters?.amenities} showTitle={false}>
            {getAmenitiesMenuItems(airportFilterCategories.AMENITIES)}
            <MenuItem
              label={loungeFilters?.complimentary?.toggleText}
              type={MenuTypes.TOGGLE}
              isSelected={isComplimentaryOnlySelected(localFilters)}
              hintText={loungeFilters?.complimentary?.toggleHint}
              onClick={(e) => {
                toggleOption({
                  event: e,
                  category: airportFilterCategories.AMENITIES_COST,
                  option: filterConstantValues.COMPLIMENTARY_ONLY,
                  isSelected: isComplimentaryOnlySelected(localFilters),
                });
              }}
            />
          </MenuGroup>
        </FilterMenu>

        <FilterMenu
          TriggerIcon={IconTime}
          triggerTitle={loungeFilters?.hours}
          menuTitle={loungeFilters?.hours}
          onApplyClick={applyFiltersWithTracking}
          onResetClick={() => {
            clearLocalFiltersByCategory([airportFilterCategories.HOURS]);
          }}
          onMenuClose={resetLocalFilter}
          isFilterApplied={isHoursSelected(appliedFilters)}
          applyButtonText={applyButtonText}
          resetButtonText={loungeFilters?.actions?.reset}
        >
          <MenuGroup title={loungeFilters?.hours} showTitle={false}>
            <MenuItem
              label={open}
              type={MenuTypes.TOGGLE}
              isSelected={isHoursSelected(localFilters)}
              onClick={(e) => {
                toggleOption({
                  event: e,
                  category: airportFilterCategories.HOURS,
                  option: filterConstantValues.OPEN_NOW,
                  isSelected: isHoursSelected(localFilters),
                });
              }}
            />
          </MenuGroup>
        </FilterMenu>
      </div>

      {hasAppliedFilters && (
        <div className="scrollbar-hide -tw-mx-4 tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-overflow-x-auto tw-overscroll-contain tw-whitespace-nowrap tw-px-4 md_tw-mx-0 md_tw-px-0">
          {appliedFilters?.terminal?.map((terminal) => (
            <Tag
              key={terminal}
              isDismissible
              className={tagsClassNames}
              onClickCapture={(e) => {
                // Prevent child handlers from hijacking tracking info
                // to resolve issue where tracked events show generic
                // html element rather than the provided element/detail
                e.stopPropagation();
                removeFilterTags(e, airportFilterCategories.TERMINAL, terminal);
              }}
            >
              {terminal}
            </Tag>
          ))}
          {appliedFilters?.amenities?.map((amenityId) => {
            const amenity = amenitiesList.find((a) => a.id === amenityId);
            if (!amenity) return null;
            return (
              <Tag
                key={amenityId}
                isDismissible
                className={tagsClassNames}
                onClickCapture={(e) => {
                  // Prevent child handlers from hijacking tracking info
                  // to resolve issue where tracked events show generic
                  // html element rather than the provided element/detail
                  e.stopPropagation();
                  removeFilterTags(
                    e,
                    airportFilterCategories.AMENITIES,
                    amenityId
                  );
                }}
              >
                {amenity.name}
              </Tag>
            );
          })}
          {isComplimentaryOnlySelected(appliedFilters) && (
            <Tag
              isDismissible
              className={tagsClassNames}
              onClickCapture={(e) => {
                // Prevent child handlers from hijacking tracking info
                // to resolve issue where tracked events show generic
                // html element rather than the provided element/detail
                e.stopPropagation();
                removeFilterTags(
                  e,
                  airportFilterCategories.AMENITIES_COST,
                  filterConstantValues.COMPLIMENTARY_ONLY
                );
              }}
            >
              {loungeFilters?.complimentary?.tag}
            </Tag>
          )}
          {isHoursSelected(appliedFilters) && (
            <Tag
              isDismissible
              className={tagsClassNames}
              onClickCapture={(e) => {
                // Prevent child handlers from hijacking tracking info
                // to resolve issue where tracked events show generic
                // html element rather than the provided element/detail
                e.stopPropagation();
                removeFilterTags(
                  e,
                  airportFilterCategories.HOURS,
                  filterConstantValues.OPEN_NOW
                );
              }}
            >
              {open}
            </Tag>
          )}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- reset filters */}
          <Link
            isInline
            className="tw-flex-shrink-0"
            onClickCapture={(e) => {
              // Prevent child handlers from hijacking tracking info
              // to resolve issue where tracked events show generic
              // html element rather than the provided element/detail
              e.stopPropagation();
              e.preventDefault();
              customTrack({
                event: e,
                element: 'reset filters',
                detail: 'airport page',
              });
              applyFilters({});
            }}
          >
            {loungeFilters?.actions?.resetFilters}
          </Link>
        </div>
      )}
    </>
  );
};

AirportFilters.propTypes = {
  lounges: PropTypes.arrayOf(
    PropTypes.shape({ airport: PropTypes.shape({ iata: PropTypes.string }) })
  ).isRequired,
  localFilters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  appliedFilters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  setLocalFilters: PropTypes.func,
  applyFilters: PropTypes.func,
};

AirportFilters.defaultProps = {
  localFilters: {},
  setLocalFilters: () => {},
};

export default AirportFilters;
